import { TrendUp01, UserCircle, Users01 } from "@untitledui/icons";
import { useCandidateFilters } from "@/api/candidates";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Label } from "@/components/base/input/label";
import { PercentileSelector, type PercentileValue } from "@/components/filters/percentile-selector";
import { PillField } from "@/components/filters/pill-field";
import { FollowersRangeFilter, GmvRangeFilter } from "@/components/filters/range-filters";
import { FilterPanelContainer, FilterPanelHeader, FilterSection } from "@/components/filters/shared";
import { Accordion } from "@/components/ui/accordion";
import { Form, FormField } from "@/components/ui/form";
import { useFilters } from "@/providers/filter-provider";

type InvitationFilterPanelProps = { open: boolean; close: () => void };

export const InvitationFilterPanel = ({ open, close }: InvitationFilterPanelProps) => {
  const { reset } = useFilters();
  const { data: candidateFilters, isLoading } = useCandidateFilters();

  return (
    <FilterPanelContainer open={open}>
      <FilterPanelHeader onClear={reset} onClose={close} />
      <div className="flex-1 overflow-y-auto pt-4 px-6 pb-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingIndicator size="md" label="Loading filters..." />
          </div>
        ) : (
          <InvitationFilters candidateFilters={candidateFilters} />
        )}
      </div>
    </FilterPanelContainer>
  );
};

const InvitationFilters = ({
  candidateFilters,
}: {
  candidateFilters?: ReturnType<typeof useCandidateFilters>["data"];
}) => {
  const { form, isActive } = useFilters();

  return (
    <Form {...form}>
      <Accordion type="single" collapsible className="w-full">
        {isActive("minGmv") && isActive("maxGmv") && (
          <FilterSection id="performance" icon={<TrendUp01 className="w-5 h-5" />} title="Performance">
            <GmvRangeFilter form={form} max={candidateFilters?.maxGmv} />
          </FilterSection>
        )}

        {isActive("minFollowers") && isActive("maxFollowers") && (
          <FilterSection id="creator-overview" icon={<UserCircle className="w-5 h-5" />} title="Creator Overview">
            <FollowersRangeFilter form={form} max={candidateFilters?.maxFollowers} />
          </FilterSection>
        )}

        {(isActive("maleAudiencePercent") || isActive("femaleAudiencePercent") || isActive("age")) && (
          <FilterSection id="audience" icon={<Users01 className="w-5 h-5" />} title="Audience">
            {isActive("maleAudiencePercent") && (
              <FormField
                control={form.control}
                name="maleAudiencePercent"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm text-tertiary">Male Audience</Label>
                    <PercentileSelector
                      value={(field.value as PercentileValue) ?? "all"}
                      onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                    />
                  </div>
                )}
              />
            )}

            {isActive("femaleAudiencePercent") && (
              <FormField
                control={form.control}
                name="femaleAudiencePercent"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm text-tertiary">Female Audience</Label>
                    <PercentileSelector
                      value={(field.value as PercentileValue) ?? "all"}
                      onChange={(val) => field.onChange(val === "all" ? undefined : val)}
                    />
                  </div>
                )}
              />
            )}

            {isActive("age") && (
              <FormField
                control={form.control}
                name="age"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <Label className="text-sm text-tertiary">Audience Age Range</Label>
                    <PillField
                      value={field.value}
                      options={["18-24", "25-34", "35-44", "45-54", "55+"]}
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />
            )}
          </FilterSection>
        )}
      </Accordion>
    </Form>
  );
};
